import { APP_PREFIXES, STATUS_COLOR_MAP } from './constants';

/**
 * Determine the product identifier for RCA API calls based on testId prefix.
 * @param {object} params
 * @param {string} [params.testId]
 * @returns {string} 'automation' | 'app-automation'
 */
export const getProductRCA = ({ testId } = {}) => {
  if (testId) {
    const upper = testId.toUpperCase();
    if (APP_PREFIXES.some((prefix) => upper.startsWith(prefix))) {
      return 'app-automation';
    }
  }
  return 'automation';
};

/**
 * Find the root cause index in a timeline array.
 * Looks for level 'RC' first, falls back to 'W'.
 */
export const getRootCauseIndex = (timeline) => {
  if (!Array.isArray(timeline) || timeline.length === 0) return -1;
  let index = timeline.findIndex((item) => item.level === 'RC');
  if (index === -1) {
    index = timeline.findIndex((item) => item.level === 'W');
  }
  return index;
};

/**
 * Map test status to a background color.
 */
export const getStatusBackgroundColor = (status) => {
  if (!status) return '#666666';
  return STATUS_COLOR_MAP[status.toLowerCase()] || '#666666';
};

/**
 * Format a date string for tooltip display.
 */
export const formatDateForTooltip = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString || '';
  }
};

/**
 * Capitalize the first character of a string.
 */
export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Extract media (expected/actual screenshots) for a timeline step.
 *
 * Follows the LPA dotlapse screenshot convention: each step may carry a
 * `screenshots` array of `{ url, is_baseline }`, where the baseline snapshot
 * is the "Expected" image and the non-baseline one is the "Actual" image.
 * At most one of each is surfaced, in Expected-then-Actual order.
 *
 * @param {object} step - A single timeline entry.
 * @returns {Array<{ label: string, url: string }>} Up to two media items,
 *   only including those with a non-empty URL.
 */
export const getStepMedia = (step) => {
  const screenshots = Array.isArray(step?.screenshots) ? step.screenshots : [];

  const hasUrl = (shot) =>
    typeof shot?.url === 'string' && shot.url.trim() !== '';

  const expected = screenshots.find((shot) => shot?.is_baseline && hasUrl(shot));
  const actual = screenshots.find((shot) => !shot?.is_baseline && hasUrl(shot));

  return [
    { label: 'Expected', shot: expected },
    { label: 'Actual', shot: actual }
  ]
    .filter((item) => item.shot)
    .map((item) => ({ label: item.label, url: item.shot.url }));
};

/**
 * Check if a framework supports RCA.
 * Works for both Web Automation (pass `type`) and App Automation (pass `buildType`, `rawType`).
 * Only Selenium, Appium, and Playwright are supported.
 */
export const isRCASupportedFramework = (buildType, rawType) => {
  const supported = ['selenium', 'appium', 'playwright'];
  const effectiveType = (buildType === 'raw' && rawType) ? rawType : buildType;
  return supported.includes(effectiveType?.toLowerCase());
};
